import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * BrokerCredentialsPage — P1-05 round 1.
 *
 * Two-pane operator surface for `apps.metadata.broker`:
 *
 *   ┌─────────────┬───────────────────────────────────────────┐
 *   │ App picker  │  Read view (definition list)              │
 *   │ (col-md-4)  │  or Edit form (col-md-8)                  │
 *   └─────────────┴───────────────────────────────────────────┘
 *
 * Talks exclusively to:
 *   GET   /v1/admin/apps                         → app list (picker)
 *   GET   /v1/admin/apps/:appId/broker           → current broker config
 *   PATCH /v1/admin/apps/:appId/broker           → set/clear/replace
 *
 * Security boundary: the page only ever surfaces the SECRET NAMES
 * (certSecretName, keySecretName, caCertSecretName). Resolved PEM bodies
 * stay in the runtime resolver / SecretsManager — never on the admin
 * surface. See P1-NF02 + the broker-credentials runbook.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
const EMPTY_DRAFT = {
    kind: 'aws_iot',
    endpoint: '',
    port: '',
    clientId: '',
    certSecretName: '',
    keySecretName: '',
    caCertSecretName: '',
    keepaliveSeconds: '',
    reconnectPeriodSeconds: '',
    subscribeTopicsText: '',
};
function brokerToDraft(b) {
    return {
        kind: b.kind,
        endpoint: b.endpoint,
        port: b.port != null ? String(b.port) : '',
        clientId: b.clientId ?? '',
        certSecretName: b.certSecretName,
        keySecretName: b.keySecretName,
        caCertSecretName: b.caCertSecretName ?? '',
        keepaliveSeconds: b.keepaliveSeconds != null ? String(b.keepaliveSeconds) : '',
        reconnectPeriodSeconds: b.reconnectPeriodSeconds != null ? String(b.reconnectPeriodSeconds) : '',
        subscribeTopicsText: (b.subscribeTopics ?? []).join('\n'),
    };
}
/**
 * Build the wire payload from a draft. Drops empty optionals so the
 * server-side schema validator doesn't reject empty-string ports etc.
 * Numeric coercion is intentionally permissive — invalid numbers go
 * across as NaN and the server's INVALID_BROKER_METADATA surfaces them
 * inline; we don't double-implement validation client-side.
 */
function draftToBroker(d) {
    const out = {
        kind: d.kind,
        endpoint: d.endpoint.trim(),
        certSecretName: d.certSecretName.trim(),
        keySecretName: d.keySecretName.trim(),
    };
    if (d.port.trim())
        out.port = Number(d.port);
    if (d.clientId.trim())
        out.clientId = d.clientId.trim();
    if (d.caCertSecretName.trim())
        out.caCertSecretName = d.caCertSecretName.trim();
    if (d.keepaliveSeconds.trim())
        out.keepaliveSeconds = Number(d.keepaliveSeconds);
    if (d.reconnectPeriodSeconds.trim()) {
        out.reconnectPeriodSeconds = Number(d.reconnectPeriodSeconds);
    }
    const topics = d.subscribeTopicsText
        .split('\n')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
    if (topics.length > 0)
        out.subscribeTopics = topics;
    return out;
}
/**
 * Map a server validation error's JSON pointer onto our form field key,
 * e.g. '/broker/endpoint' → 'endpoint'. Returns null for errors that
 * don't anchor to a specific field (we'll surface those at the top).
 */
function errorFieldKey(err) {
    const raw = err.path ?? err.field ?? '';
    // '/broker/endpoint' → 'endpoint'; 'broker.endpoint' → 'endpoint'.
    const segs = raw.replace(/^\/+/, '').split(/[./]/).filter(Boolean);
    const last = segs[segs.length - 1];
    switch (last) {
        case 'kind':
        case 'endpoint':
        case 'port':
        case 'clientId':
        case 'certSecretName':
        case 'keySecretName':
        case 'caCertSecretName':
        case 'keepaliveSeconds':
        case 'reconnectPeriodSeconds':
            return last;
        case 'subscribeTopics':
            return 'subscribeTopicsText';
        default:
            return null;
    }
}
export function BrokerCredentialsPage() {
    const fetcher = useNexusFetch();
    // ---- App list -----------------------------------------------------
    const [apps, setApps] = useState(null);
    const [appsError, setAppsError] = useState(null);
    const [selectedAppId, setSelectedAppId] = useState(null);
    const loadApps = useCallback(async () => {
        setAppsError(null);
        try {
            const res = await fetcher('/v1/admin/apps');
            const list = res?.apps ?? [];
            setApps(list);
            // Auto-select first app on initial load (or after the current
            // selection vanishes from the list).
            setSelectedAppId((prev) => {
                if (prev && list.some((a) => a.appId === prev))
                    return prev;
                return list[0]?.appId ?? null;
            });
        }
        catch (e) {
            setApps([]);
            setAppsError(e instanceof Error ? e.message : 'failed to load apps');
        }
    }, [fetcher]);
    useEffect(() => {
        void loadApps();
    }, [loadApps]);
    // ---- Selected app's broker config --------------------------------
    const [broker, setBroker] = useState(null);
    const [brokerLoading, setBrokerLoading] = useState(false);
    const [brokerError, setBrokerError] = useState(null);
    const [mode, setMode] = useState('read');
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [submitting, setSubmitting] = useState(false);
    const [formBanner, setFormBanner] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [generalErrors, setGeneralErrors] = useState([]);
    const loadBroker = useCallback(async (appId) => {
        setBrokerLoading(true);
        setBrokerError(null);
        setMode('read');
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
        try {
            const res = await fetcher(`/v1/admin/apps/${encodeURIComponent(appId)}/broker`);
            setBroker(res?.broker ?? null);
        }
        catch (e) {
            setBroker(null);
            setBrokerError(e instanceof Error ? e.message : 'failed to load broker config');
        }
        finally {
            setBrokerLoading(false);
        }
    }, [fetcher]);
    useEffect(() => {
        if (!selectedAppId) {
            setBroker(null);
            return;
        }
        void loadBroker(selectedAppId);
    }, [selectedAppId, loadBroker]);
    // ---- Edit-mode handlers ------------------------------------------
    const startEdit = useCallback(() => {
        setDraft(broker ? brokerToDraft(broker) : EMPTY_DRAFT);
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
        setMode('edit');
    }, [broker]);
    const cancelEdit = useCallback(() => {
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
        setMode('read');
    }, []);
    const updateField = useCallback((key, value) => {
        setDraft((d) => ({ ...d, [key]: value }));
        // Clear stale field-level errors for this key on edit so the
        // operator can see them resolved as they retype.
        setFieldErrors((fe) => {
            if (!fe[key])
                return fe;
            const next = { ...fe };
            delete next[key];
            return next;
        });
    }, []);
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!selectedAppId)
            return;
        setSubmitting(true);
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
        try {
            const payload = { broker: draftToBroker(draft) };
            await fetcher(`/v1/admin/apps/${encodeURIComponent(selectedAppId)}/broker`, {
                method: 'PATCH',
                json: payload,
            });
            await loadBroker(selectedAppId);
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                if (err.code === 'INVALID_BROKER_METADATA' && Array.isArray(err.errors)) {
                    const byField = {};
                    const general = [];
                    for (const e2 of err.errors) {
                        const key = errorFieldKey(e2);
                        if (key) {
                            (byField[key] ??= []).push(e2.message);
                        }
                        else {
                            general.push(e2.message);
                        }
                    }
                    setFieldErrors(byField);
                    setGeneralErrors(general);
                    setFormBanner(err.message || 'broker shape failed validation');
                }
                else {
                    setFormBanner(`${err.code}: ${err.message}`);
                }
            }
            else {
                setFormBanner(err instanceof Error ? err.message : 'request failed (unknown error)');
            }
        }
        finally {
            setSubmitting(false);
        }
    }, [draft, fetcher, loadBroker, selectedAppId]);
    const handleClear = useCallback(async () => {
        if (!selectedAppId)
            return;
        if (!window.confirm(`Clear broker config for "${selectedAppId}"? The translator runtime ` +
            `will stop publishing for this app until a new config is set.`)) {
            return;
        }
        setSubmitting(true);
        setFormBanner(null);
        try {
            await fetcher(`/v1/admin/apps/${encodeURIComponent(selectedAppId)}/broker`, {
                method: 'PATCH',
                json: { broker: null },
            });
            await loadBroker(selectedAppId);
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                setFormBanner(`${err.code}: ${err.message}`);
            }
            else {
                setFormBanner(err instanceof Error ? err.message : 'clear failed');
            }
        }
        finally {
            setSubmitting(false);
        }
    }, [fetcher, loadBroker, selectedAppId]);
    // ---- Derived ------------------------------------------------------
    const selectedAppDisplay = useMemo(() => {
        if (!selectedAppId || !apps)
            return null;
        return apps.find((a) => a.appId === selectedAppId) ?? null;
    }, [apps, selectedAppId]);
    // ---- Render -------------------------------------------------------
    return (_jsxs("div", { children: [_jsx("h1", { className: "h3 mb-3", children: "Broker credentials" }), _jsxs("p", { className: "text-muted small mb-4", children: ["Operator surface for ", _jsx("code", { children: "apps.metadata.broker" }), ". The values here are ", _jsx("strong", { children: "secret names" }), " only \u2014 the runtime resolver fetches the actual cert / key / CA from the secrets backend at startup."] }), _jsxs("div", { className: "row g-3", children: [_jsx("div", { className: "col-md-4", children: _jsxs("div", { className: "card", children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsx("span", { className: "fw-semibold", children: "Apps" }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", onClick: () => void loadApps(), disabled: apps === null, children: "Refresh" })] }), apps === null && (_jsxs("div", { className: "card-body text-muted small", children: [_jsx("div", { className: "spinner-border spinner-border-sm me-2", role: "status" }), "Loading apps\u2026"] })), apps !== null && appsError !== null && (_jsxs("div", { className: "card-body", children: [_jsx("div", { className: "alert alert-danger small mb-2", children: appsError }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void loadApps(), children: "Retry" })] })), apps !== null && appsError === null && apps.length === 0 && (_jsx("div", { className: "card-body text-muted small", children: "No apps registered." })), apps !== null && appsError === null && apps.length > 0 && (_jsx("ul", { className: "list-group list-group-flush", children: apps.map((a) => {
                                        const active = a.appId === selectedAppId;
                                        return (_jsx("li", { className: `list-group-item list-group-item-action ${active ? 'active' : ''}`, role: "button", onClick: () => setSelectedAppId(a.appId), style: { cursor: 'pointer' }, children: _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { children: [_jsx("div", { className: "fw-semibold", children: a.displayName }), _jsx("code", { className: active ? 'text-light small' : 'text-muted small', children: a.appId })] }), a.status !== 'active' && (_jsx("span", { className: "badge bg-secondary", children: a.status }))] }) }, a.appId));
                                    }) }))] }) }), _jsxs("div", { className: "col-md-8", children: [!selectedAppId && (_jsx("div", { className: "card", children: _jsx("div", { className: "card-body text-muted", children: "Select an app on the left to view or edit its broker config." }) })), selectedAppId && (_jsxs("div", { className: "card", children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsxs("div", { children: [_jsx("span", { className: "fw-semibold", children: selectedAppDisplay?.displayName ?? selectedAppId }), ' ', _jsx("code", { className: "text-muted small", children: selectedAppId })] }), mode === 'read' && !brokerLoading && (_jsxs("div", { className: "d-flex gap-2", children: [_jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: startEdit, children: broker ? 'Edit' : 'Set broker config' }), broker && (_jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void handleClear(), disabled: submitting, children: "Clear broker config" }))] }))] }), _jsxs("div", { className: "card-body", children: [brokerLoading && (_jsxs("div", { className: "text-muted small", children: [_jsx("div", { className: "spinner-border spinner-border-sm me-2", role: "status" }), "Loading broker config\u2026"] })), !brokerLoading && brokerError !== null && (_jsxs(_Fragment, { children: [_jsx("div", { className: "alert alert-danger", children: brokerError }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void loadBroker(selectedAppId), children: "Retry" })] })), !brokerLoading && brokerError === null && mode === 'read' && (_jsx(ReadView, { broker: broker })), !brokerLoading && brokerError === null && mode === 'edit' && (_jsx(EditForm, { draft: draft, fieldErrors: fieldErrors, generalErrors: generalErrors, formBanner: formBanner, submitting: submitting, onChange: updateField, onCancel: cancelEdit, onSubmit: handleSubmit })), !brokerLoading && brokerError === null && mode === 'read' && formBanner && (_jsx("div", { className: "alert alert-danger mt-3", children: formBanner }))] })] }))] })] })] }));
}
// ---------------------------------------------------------------------
// Read view
function ReadView({ broker }) {
    if (!broker) {
        return (_jsxs("div", { className: "text-muted", children: [_jsx("p", { children: "No broker configured for this app." }), _jsxs("p", { className: "small mb-0", children: ["Click ", _jsx("em", { children: "Set broker config" }), " above to configure one."] })] }));
    }
    return (_jsxs("dl", { className: "row mb-0", children: [_jsx(Term, { label: "Kind", children: _jsx("span", { className: "badge bg-info text-dark", children: broker.kind }) }), _jsx(Term, { label: "Endpoint", children: _jsx("code", { children: broker.endpoint }) }), _jsx(Term, { label: "Port", children: broker.port != null ? (_jsx("code", { children: broker.port })) : (_jsx("span", { className: "text-muted small", children: "default (8883)" })) }), _jsx(Term, { label: "Client ID", children: broker.clientId ? (_jsx("code", { children: broker.clientId })) : (_jsx("span", { className: "text-muted small", children: "default (nexus-<appId>)" })) }), _jsx(Term, { label: "Cert secret", children: _jsx("code", { children: broker.certSecretName }) }), _jsx(Term, { label: "Key secret", children: _jsx("code", { children: broker.keySecretName }) }), _jsx(Term, { label: "CA cert secret", children: broker.caCertSecretName ? (_jsx("code", { children: broker.caCertSecretName })) : (_jsx("span", { className: "text-muted small", children: "\u2014 none \u2014" })) }), _jsx(Term, { label: "Keepalive", children: broker.keepaliveSeconds != null ? (_jsxs("code", { children: [broker.keepaliveSeconds, "s"] })) : (_jsx("span", { className: "text-muted small", children: "default (30s)" })) }), _jsx(Term, { label: "Reconnect period", children: broker.reconnectPeriodSeconds != null ? (_jsxs("code", { children: [broker.reconnectPeriodSeconds, "s"] })) : (_jsx("span", { className: "text-muted small", children: "default (5s)" })) }), _jsx(Term, { label: "Subscribe topics", children: broker.subscribeTopics && broker.subscribeTopics.length > 0 ? (_jsx("ul", { className: "list-unstyled mb-0", children: broker.subscribeTopics.map((t) => (_jsx("li", { children: _jsx("code", { children: t }) }, t))) })) : (_jsx("span", { className: "text-muted small", children: "\u2014 none \u2014" })) })] }));
}
function Term({ label, children }) {
    return (_jsxs(_Fragment, { children: [_jsx("dt", { className: "col-sm-4 fw-normal text-muted", children: label }), _jsx("dd", { className: "col-sm-8 mb-2", children: children })] }));
}
function EditForm({ draft, fieldErrors, generalErrors, formBanner, submitting, onChange, onCancel, onSubmit, }) {
    return (_jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [formBanner && _jsx("div", { className: "alert alert-danger", children: formBanner }), generalErrors.length > 0 && (_jsxs("div", { className: "alert alert-warning", children: [_jsx("div", { className: "fw-semibold mb-1", children: "Server validation errors:" }), _jsx("ul", { className: "mb-0 small", children: generalErrors.map((m, i) => (_jsx("li", { children: m }, i))) })] })), _jsx(Field, { label: "Kind", required: true, errors: fieldErrors.kind, hint: "Broker provider. Only aws_iot is supported today.", children: _jsx("select", { className: fieldClass('form-select', fieldErrors.kind), value: draft.kind, onChange: (e) => onChange('kind', e.target.value), children: _jsx("option", { value: "aws_iot", children: "aws_iot" }) }) }), _jsx(Field, { label: "Endpoint", required: true, errors: fieldErrors.endpoint, hint: "e.g. a1b2c3d4-ats.iot.us-east-1.amazonaws.com", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.endpoint), value: draft.endpoint, onChange: (e) => onChange('endpoint', e.target.value), autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "Port", errors: fieldErrors.port, hint: "Optional; defaults to 8883.", children: _jsx("input", { type: "number", inputMode: "numeric", className: fieldClass('form-control', fieldErrors.port), value: draft.port, onChange: (e) => onChange('port', e.target.value) }) }), _jsx(Field, { label: "Client ID", errors: fieldErrors.clientId, hint: "Optional; defaults to nexus-<appId>.", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.clientId), value: draft.clientId, onChange: (e) => onChange('clientId', e.target.value), autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "Cert secret name", required: true, errors: fieldErrors.certSecretName, hint: "Name in the secrets backend. PEM body is never shown here.", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.certSecretName), value: draft.certSecretName, onChange: (e) => onChange('certSecretName', e.target.value), autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "Key secret name", required: true, errors: fieldErrors.keySecretName, hint: "Name in the secrets backend.", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.keySecretName), value: draft.keySecretName, onChange: (e) => onChange('keySecretName', e.target.value), autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "CA cert secret name", errors: fieldErrors.caCertSecretName, hint: "Optional. Omit to use the platform's bundled AWS IoT root CA.", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.caCertSecretName), value: draft.caCertSecretName, onChange: (e) => onChange('caCertSecretName', e.target.value), autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "Keepalive seconds", errors: fieldErrors.keepaliveSeconds, hint: "Optional; defaults to 30.", children: _jsx("input", { type: "number", inputMode: "numeric", className: fieldClass('form-control', fieldErrors.keepaliveSeconds), value: draft.keepaliveSeconds, onChange: (e) => onChange('keepaliveSeconds', e.target.value) }) }), _jsx(Field, { label: "Reconnect period seconds", errors: fieldErrors.reconnectPeriodSeconds, hint: "Optional; defaults to 5.", children: _jsx("input", { type: "number", inputMode: "numeric", className: fieldClass('form-control', fieldErrors.reconnectPeriodSeconds), value: draft.reconnectPeriodSeconds, onChange: (e) => onChange('reconnectPeriodSeconds', e.target.value) }) }), _jsx(Field, { label: "Subscribe topics", errors: fieldErrors.subscribeTopicsText, hint: "One topic per line. Leave empty for none.", children: _jsx("textarea", { className: fieldClass('form-control', fieldErrors.subscribeTopicsText), value: draft.subscribeTopicsText, onChange: (e) => onChange('subscribeTopicsText', e.target.value), rows: 4, spellCheck: false, style: { fontFamily: 'monospace' } }) }), _jsxs("div", { className: "d-flex gap-2 mt-3", children: [_jsx("button", { type: "submit", className: "btn btn-primary", disabled: submitting, children: submitting ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Saving\u2026"] })) : ('Save') }), _jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: onCancel, disabled: submitting, children: "Cancel" })] })] }));
}
function fieldClass(base, errs) {
    return errs && errs.length > 0 ? `${base} is-invalid` : base;
}
function Field({ label, required, errors, hint, children }) {
    return (_jsxs("div", { className: "mb-3", children: [_jsxs("label", { className: "form-label mb-1", children: [label, required && _jsx("span", { className: "text-danger ms-1", children: "*" })] }), children, hint && !errors?.length && _jsx("div", { className: "form-text small", children: hint }), errors?.map((m, i) => (_jsx("div", { className: "invalid-feedback d-block small", children: m }, i)))] }));
}
//# sourceMappingURL=BrokerCredentialsPage.js.map